import { cn } from '@core/lib/cn';

export interface ProgressBarProps {
  /** 0-100. Omit for an indeterminate bar (work started, duration unknown). */
  value?: number;
  /** Accessible name, e.g. "Upload progress". Required — a bare bar means nothing. */
  label: string;
  /** Shows the percentage next to the label. */
  showValue?: boolean;
  className?: string;
}

/**
 * Upload and validation progress.
 *
 * Uses `role="progressbar"` with real aria-value* attributes rather than a styled
 * div, so assistive tech can report "62 percent" on demand. The determinate case
 * clamps to whole percent: a bar that reports 61.8371% is noise, and a jittering
 * live value is worse than none.
 */
export function ProgressBar({ value, label, showValue = false, className }: ProgressBarProps) {
  const indeterminate = value === undefined || !Number.isFinite(value);
  const clamped = indeterminate ? 0 : Math.max(0, Math.min(100, Math.round(value)));

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-ink">{label}</span>
        {showValue && !indeterminate ? (
          <span className="text-sm tabular-nums text-ink-muted">{clamped}%</span>
        ) : null}
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={indeterminate ? undefined : 0}
        aria-valuemax={indeterminate ? undefined : 100}
        aria-valuenow={indeterminate ? undefined : clamped}
        aria-valuetext={indeterminate ? 'In progress' : `${clamped}%`}
        className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
      >
        <div
          className={cn(
            // Gold is 1.57:1 against the track, which fails WCAG 1.4.11's 3:1
            // for non-text UI. The inset dark outline is the brand's own
            // device — every gold shape in the logo is outlined — and carries
            // the boundary so the fill can stay brand gold.
            'h-full rounded-full bg-brand ring-1 ring-inset ring-brand-edge',
            'transition-[width] duration-300 ease-out',
            indeterminate && 'w-1/3 animate-shimmer',
          )}
          style={indeterminate ? undefined : { width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
